import { FC, SyntheticEvent, useMemo } from "react";
import { useIntl } from "react-intl";
import { useSearchParams } from "react-router-dom";

import { Box, Tooltip } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";

import { useAsu } from "@framework/provider-asu";
import { useSettings } from "@framework/provider-settings";
import { useUser } from "@framework/provider-user";
import { IUser } from "@framework/types";

import { StyledTab } from "../../../../../../components/common/styled/tab";
import { DashboardAccordion } from "../../ui/accordion";
import { TrainEventsSchemeTable } from "../events-train";
import { TrainArrangements } from "../train-arrangements";
import { Defects } from "../defects";
import { Recommendations } from "../recommendations";
import { Reports } from "../reports";
import { OpenableWindow } from "../../ui/openable-window";

interface ITablesProps {
  isNarrow?: boolean;
}

export const Tables: FC<ITablesProps> = ({ isNarrow }) => {
  const settings = useSettings();
  const { profile } = useUser<IUser>();

  const { tablesActiveTab, changeTablesActiveTab } = useAsu();

  const [searchParams] = useSearchParams();
  const { formatMessage } = useIntl();

  const schemeState = settings.getSchemeState();
  const { tables } = schemeState;
  const onlyPage = searchParams.get("onlyPage") === "true";
  const currentComponent = searchParams.get("component");
  const title = formatMessage({ id: "pages.scheme.tables.title" });

  const shouldShowComponent = useMemo(() => {
    if (!onlyPage) return true;
    if (currentComponent) return currentComponent === "tables";
    return !!profile.interfaceSettings.showTableData;
  }, [onlyPage, currentComponent, profile.interfaceSettings.showTableData]);

  const setExpanded = (isExpanded: boolean): void => {
    const newSchemeState = {
      ...schemeState,
      tables: isExpanded,
    };

    settings.setSchemeState(newSchemeState);
  };

  const handleTabChange = (event: SyntheticEvent, newValue: string) => {
    changeTablesActiveTab(newValue);
  };

  if (!shouldShowComponent) {
    return null;
  }

  const tabPanelSx = {
    p: 0,
    pb: 1,
    flex: 1,
    minHeight: 0,
    height: "100%",
    overflow: "hidden",
    "&.MuiTabPanel-hidden": {
      display: "none",
    },
  };

  const tabPanelContentSx = {
    height: "100%",
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  };

  const content = (
    <TabContext value={tablesActiveTab}>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
        <Box sx={{ m: -2, p: 2, flexShrink: 0 }}>
          <TabList
            onChange={handleTabChange}
            aria-label="journals tabs"
            TabIndicatorProps={{ sx: { display: "none" } }}
            sx={{ minHeight: 30, mt: 0.5 }}
          >
            <StyledTab
              sx={{ minHeight: 30, p: "0 16px" }}
              label={
                <Tooltip title={formatMessage({ id: "pages.scheme.events.title" })} arrow>
                  <Box style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>
                    {formatMessage({ id: "pages.scheme.events.title" })}
                  </Box>
                </Tooltip>
              }
              value="1"
            />
            <StyledTab
              sx={{ minHeight: 30, p: "0 16px" }}
              label={
                <Tooltip title={formatMessage({ id: "pages.scheme.trainArrangements.title" })} arrow>
                  <Box style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>
                    {formatMessage({ id: "pages.scheme.trainArrangements.title" })}
                  </Box>
                </Tooltip>
              }
              value="2"
            />
            <StyledTab
              sx={{ minHeight: 30, p: "0 16px" }}
              label={
                <Tooltip title={formatMessage({ id: "pages.scheme.defects.title" })} arrow>
                  <Box style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>
                    {formatMessage({ id: "pages.scheme.defects.title" })}
                  </Box>
                </Tooltip>
              }
              value="3"
            />
            <StyledTab
              sx={{ minHeight: 30, p: "0 16px" }}
              label={
                <Tooltip title={formatMessage({ id: "pages.scheme.recommendations.title" })} arrow>
                  <Box style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>
                    {formatMessage({ id: "pages.scheme.recommendations.title" })}
                  </Box>
                </Tooltip>
              }
              value="4"
            />
            <StyledTab
              sx={{ minHeight: 30, p: "0 16px" }}
              label={
                <Tooltip title={formatMessage({ id: "pages.scheme.reports.title" })} arrow>
                  <Box style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>
                    {formatMessage({ id: "pages.scheme.reports.title" })}
                  </Box>
                </Tooltip>
              }
              value="5"
            />
          </TabList>
        </Box>
        <TabPanel value="1" sx={tabPanelSx}>
          <Box sx={tabPanelContentSx}>
            <TrainEventsSchemeTable embedded isActiveTableTab={tablesActiveTab === "1"} />
          </Box>
        </TabPanel>
        <TabPanel value="2" sx={tabPanelSx}>
          <Box sx={tabPanelContentSx}>
            <TrainArrangements isActiveTableTab={tablesActiveTab === "2"} />
          </Box>
        </TabPanel>
        <TabPanel value="3" sx={tabPanelSx}>
          <Box sx={tabPanelContentSx}>
            <Defects isActiveTableTab={tablesActiveTab === "3"} />
          </Box>
        </TabPanel>
        <TabPanel value="4" sx={tabPanelSx}>
          <Box sx={tabPanelContentSx}>
            <Recommendations isActiveTableTab={tablesActiveTab === "4"} />
          </Box>
        </TabPanel>
        <TabPanel value="5" sx={tabPanelSx}>
          <Box sx={tabPanelContentSx}>
            <Reports isActiveTableTab={tablesActiveTab === "5"} />
          </Box>
        </TabPanel>
      </Box>
    </TabContext>
  );

  return onlyPage && !!currentComponent ? (
    <OpenableWindow title={title} componentName="tables">
      {content}
    </OpenableWindow>
  ) : (
    <DashboardAccordion
      expanded={tables}
      title={title}
      setExpanded={setExpanded}
      componentName="tables"
      isNarrow={isNarrow}
    >
      {content}
    </DashboardAccordion>
  );
};
